import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from '@iptv/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';

import { adminFetch, type ApiError } from '../lib/api';
import { useTotpChallenge } from '../lib/totp';
import { SortableTh, useTableSort } from '../lib/useTableSort';

interface CouponRow {
  id: number;
  code: string;
  discount_type: 'percent' | 'fixed';
  discount_value: number;
  uses_count: number;
  max_uses: number | null;
  valid_from: string | null;
  valid_until: string | null;
  applies_to_site_slugs: string[] | null;
  applies_to_plan_codes: string[] | null;
  active: boolean;
  created_at: string;
}

interface CouponForm {
  code: string;
  discount_type: 'percent' | 'fixed';
  discount_value: number;
  max_uses: number | null;
  valid_from: string;
  valid_until: string;
  applies_to_site_slugs: string;
  applies_to_plan_codes: string;
}

const EMPTY_FORM: CouponForm = {
  code: '',
  discount_type: 'percent',
  discount_value: 10,
  max_uses: null,
  valid_from: '',
  valid_until: '',
  applies_to_site_slugs: '',
  applies_to_plan_codes: '',
};

function couponToForm(c: CouponRow): CouponForm {
  return {
    code: c.code,
    discount_type: c.discount_type,
    discount_value: c.discount_value,
    max_uses: c.max_uses,
    valid_from: c.valid_from ? c.valid_from.slice(0, 10) : '',
    valid_until: c.valid_until ? c.valid_until.slice(0, 10) : '',
    applies_to_site_slugs: (c.applies_to_site_slugs ?? []).join(', '),
    applies_to_plan_codes: (c.applies_to_plan_codes ?? []).join(', '),
  };
}

function splitCsv(s: string): string[] | null {
  const parts = s
    .split(',')
    .map((x) => x.trim())
    .filter(Boolean);
  return parts.length > 0 ? parts : null;
}

function formToPayload(f: CouponForm): Record<string, unknown> {
  return {
    code: f.code.trim().toUpperCase(),
    discount_type: f.discount_type,
    discount_value: Number(f.discount_value),
    max_uses: f.max_uses ? Number(f.max_uses) : null,
    valid_from: f.valid_from || null,
    valid_until: f.valid_until || null,
    applies_to_site_slugs: splitCsv(f.applies_to_site_slugs),
    applies_to_plan_codes: splitCsv(f.applies_to_plan_codes),
  };
}

function formatDiscount(c: CouponRow): string {
  if (c.discount_type === 'percent') return `${c.discount_value}% off`;
  return `$${(c.discount_value / 100).toFixed(2)} off`;
}

export function CouponsPage() {
  const queryClient = useQueryClient();
  const challengeTotp = useTotpChallenge();
  const [editing, setEditing] = useState<CouponRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<CouponForm>(EMPTY_FORM);

  const coupons = useQuery<CouponRow[]>({
    queryKey: ['admin', 'coupons'],
    queryFn: () => adminFetch<CouponRow[]>('/api/v1/admin/coupons'),
  });
  const { sorted, sortKey, sortDir, toggleSort } = useTableSort<CouponRow>(coupons.data, {
    initialKey: 'code',
    initialDir: 'asc',
  });

  const create = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      adminFetch<CouponRow>('/api/v1/admin/coupons', { method: 'POST', body: payload }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'coupons'] });
      setCreating(false);
      setForm(EMPTY_FORM);
    },
  });
  const update = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: Record<string, unknown> }) =>
      adminFetch<CouponRow>(`/api/v1/admin/coupons/${id}`, { method: 'PATCH', body: payload }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'coupons'] });
      setEditing(null);
    },
  });
  const remove = useMutation({
    mutationFn: ({ id, token }: { id: number; token: string }) =>
      adminFetch(`/api/v1/admin/coupons/${id}?totp_challenge_token=${encodeURIComponent(token)}`, {
        method: 'DELETE',
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'coupons'] }),
  });

  function openCreate() {
    setForm(EMPTY_FORM);
    setCreating(true);
  }
  function openEdit(c: CouponRow) {
    setForm(couponToForm(c));
    setEditing(c);
  }
  async function handleDelete(c: CouponRow) {
    const token = await challengeTotp({
      message: `Delete coupon "${c.code}". Existing redemptions are kept in the audit log.`,
    });
    if (!token) return;
    try {
      await remove.mutateAsync({ id: c.id, token });
    } catch (err) {
      alert((err as ApiError).detail ?? 'Delete failed');
    }
  }
  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const payload = formToPayload(form);
    if (editing) await update.mutateAsync({ id: editing.id, payload });
    else await create.mutateAsync(payload);
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-end">
        <Button onClick={openCreate}>
          <Plus className="h-4 w-4" /> New coupon
        </Button>
      </div>
      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full text-sm">
          <thead className="bg-secondary/50">
            <tr>
              <SortableTh sortKey="code" current={sortKey} dir={sortDir} onSort={toggleSort}>
                Code
              </SortableTh>
              <SortableTh
                sortKey="discount_value"
                current={sortKey}
                dir={sortDir}
                onSort={toggleSort}
              >
                Discount
              </SortableTh>
              <SortableTh sortKey="uses_count" current={sortKey} dir={sortDir} onSort={toggleSort}>
                Uses
              </SortableTh>
              <th className="px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                Sites
              </th>
              <SortableTh sortKey="valid_until" current={sortKey} dir={sortDir} onSort={toggleSort}>
                Expires
              </SortableTh>
              <SortableTh sortKey="active" current={sortKey} dir={sortDir} onSort={toggleSort}>
                Active
              </SortableTh>
              <th className="px-3 py-2 text-right text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {coupons.isPending && (
              <tr>
                <td colSpan={7} className="px-3 py-4 text-center text-muted-foreground">
                  Loading…
                </td>
              </tr>
            )}
            {coupons.data?.length === 0 && (
              <tr>
                <td colSpan={7} className="px-3 py-4 text-center text-muted-foreground">
                  No coupons yet.
                </td>
              </tr>
            )}
            {sorted.map((c) => (
              <tr key={c.id} className="border-t">
                <td className="px-3 py-2 font-mono text-xs">{c.code}</td>
                <td className="px-3 py-2">{formatDiscount(c)}</td>
                <td className="px-3 py-2">
                  {c.uses_count}/{c.max_uses ?? '∞'}
                </td>
                <td className="px-3 py-2 text-xs text-muted-foreground">
                  {c.applies_to_site_slugs?.join(', ') || 'all'}
                </td>
                <td className="px-3 py-2 text-xs">
                  {c.valid_until ? new Date(c.valid_until).toLocaleDateString() : '—'}
                </td>
                <td className="px-3 py-2">
                  {c.active ? (
                    <Badge variant="success">yes</Badge>
                  ) : (
                    <Badge variant="secondary">no</Badge>
                  )}
                </td>
                <td className="px-3 py-2 text-right">
                  <div className="flex justify-end gap-1">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(c)}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDelete(c)}
                      disabled={remove.isPending}
                      title={
                        c.active
                          ? 'Soft-delete this coupon'
                          : 'Already deleted — click to delete again'
                      }
                    >
                      <Trash2
                        className={`h-3.5 w-3.5 ${c.active ? '' : 'text-muted-foreground'}`}
                      />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog
        open={creating || editing !== null}
        onOpenChange={(open) => {
          if (!open) {
            setCreating(false);
            setEditing(null);
          }
        }}
      >
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>{editing ? `Edit ${editing.code}` : 'New coupon'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="grid gap-3 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="code">Code</Label>
              <Input
                id="code"
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
                disabled={editing !== null}
                placeholder="WAIT5"
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="discount_type">Type</Label>
              <select
                id="discount_type"
                value={form.discount_type}
                onChange={(e) =>
                  setForm({ ...form, discount_type: e.target.value as 'percent' | 'fixed' })
                }
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value="percent">Percent (0–100)</option>
                <option value="fixed">Fixed amount (cents)</option>
              </select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="discount_value">
                Value ({form.discount_type === 'percent' ? '%' : 'cents'})
              </Label>
              <Input
                id="discount_value"
                type="number"
                min={0}
                value={form.discount_value}
                onChange={(e) => setForm({ ...form, discount_value: Number(e.target.value) })}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="max_uses">Max uses (empty = ∞)</Label>
              <Input
                id="max_uses"
                type="number"
                min={1}
                value={form.max_uses ?? ''}
                onChange={(e) =>
                  setForm({
                    ...form,
                    max_uses: e.target.value ? Number(e.target.value) : null,
                  })
                }
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="valid_from">Valid from</Label>
              <Input
                id="valid_from"
                type="date"
                value={form.valid_from}
                onChange={(e) => setForm({ ...form, valid_from: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="valid_until">Valid until</Label>
              <Input
                id="valid_until"
                type="date"
                value={form.valid_until}
                onChange={(e) => setForm({ ...form, valid_until: e.target.value })}
              />
            </div>
            <div className="space-y-1.5 md:col-span-2">
              <Label htmlFor="sites">Applies to site slugs (comma sep, empty = all)</Label>
              <Input
                id="sites"
                value={form.applies_to_site_slugs}
                placeholder="wc-en, wc-es"
                onChange={(e) => setForm({ ...form, applies_to_site_slugs: e.target.value })}
              />
            </div>
            <div className="space-y-1.5 md:col-span-2">
              <Label htmlFor="plans">Applies to plan codes (comma sep, empty = all)</Label>
              <Input
                id="plans"
                value={form.applies_to_plan_codes}
                onChange={(e) => setForm({ ...form, applies_to_plan_codes: e.target.value })}
              />
            </div>
            {(create.isError || update.isError) && (
              <div className="md:col-span-2 rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {String(
                  (create.error as ApiError | null)?.detail ??
                    (update.error as ApiError | null)?.detail ??
                    'Something went wrong',
                )}
              </div>
            )}
            <div className="md:col-span-2 flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setCreating(false);
                  setEditing(null);
                }}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={create.isPending || update.isPending}>
                {create.isPending || update.isPending
                  ? 'Saving…'
                  : editing
                    ? 'Save changes'
                    : 'Create coupon'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
